import {
  ReactNode,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import styles from "./BottomSheet.module.scss";
import CloseIcon from "@/assets/icons/common/CloseIcon";
import { OVERLAY_PANEL_MS } from "@/shared/lib/motion";
import { resolveOverlayBackAction } from "@/shared/lib/overlayBackAction";
import { blockActivationKeys } from "@/shared/lib/overlayInput";
import { useBodyScrollLock } from "@/shared/hooks/useBodyScrollLock";
import { useOverlayBackRegistration } from "@/shared/hooks/useOverlayBackRegistration";
import { useOverlayPresence } from "@/shared/hooks/useOverlayPresence";
import { useTapInteraction } from "@/shared/ui/pressable";

export type BottomSheetPresentation = "partial" | "full";
export type BottomSheetTheme = "dark" | "light";
/**
 * sheet의 화면 배치 스타일.
 * - "edge"(기본): sheet가 화면 좌우/하단에 붙고 상단 모서리만 둥글다. 기존 동작.
 * - "floating": sheet가 좌우/하단에 여백을 두고 떠 있으며 네 모서리가 모두 둥글다.
 */
export type BottomSheetPlacement = "edge" | "floating";

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  presentation?: BottomSheetPresentation;
  theme?: BottomSheetTheme;
  /** 화면 배치 스타일. 기본값 "edge" — 기존 사용처 호환. */
  placementStyle?: BottomSheetPlacement;
  title?: string;
  headerRight?: ReactNode;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  /**
   * ESC 키로 sheet를 닫을 수 있는지. 기본값 true — 기존 사용처 동작 유지.
   * 결과 popup 처럼 사용자가 명시적으로 닫아야 하는 경우에만 false 로 설정.
   */
  closeOnEsc?: boolean;
  showCloseButton?: boolean;
  /**
   * close(X) 아이콘 색 오버라이드(escape hatch). 미지정 시 테마 기본색(.light/.dark .headerClose color,
   * 시트 X 표준 #B9B9B9=--theme-label-assistive) 사용. 표준과 다른 X 색이 필요한 특수 시트에서만 지정.
   */
  closeIconColor?: string;
  className?: string;
  contentClassName?: string;
  /**
   * 아래 방향 drag로 sheet를 닫을 수 있는지.
   * 기본값 false — 기존 사용처 동작 유지.
   */
  draggable?: boolean;
  /**
   * 상단 drag handle bar 노출 여부.
   * 미지정 시 draggable과 동일하게 동작.
   */
  showDragHandle?: boolean;
  /**
   * drag로 닫히는 임계 거리(px). 기본 100px.
   * 이 값 미만으로 drag 후 release하면 snap back.
   */
  dragCloseThreshold?: number;
  /**
   * 백드롭(dim) 불투명도. 미지정 시 시트 공통 0.5(CSS fallback) — D7 결정(2026-10-21)으로 시트 Dim 은 0.5 하나.
   * 정의가 다른 값을 정한 시트만 지정한다(지금은 시험 신청 진입 0.28).
   */
  backdropOpacity?: number;
  /**
   * z-index 상향 오프셋(중첩 시트용). 다른 BottomSheet 안에서 열릴 때 부모 시트(z-modal+1) 위로
   * overlay/sheet 를 올린다. 기본 0(기존 동작 유지). 예: 2 → overlay = z-modal+2, sheet = z-modal+3.
   */
  elevation?: number;
  /**
   * `full` presentation 전용 opt-in. true 면 시트가 상단 safe-area(--sat)를 흰 padding 으로
   * 예약하지 않고, 시트 배경/콘텐츠가 노치 뒤까지 흐른다(edge-to-edge top). 이때 상단 safe-area
   * 회피 책임은 콘텐츠(헤더 등)에 있다 — edge-to-edge 그라데이션 배경을 노치까지 채워야 하는
   * full 시트에서 사용. 기본값 false(기존 계약: `.full` 이 --sat 만큼 top padding 예약).
   * ⚠️ BottomSheet 내장 header(title·showCloseButton·headerRight)와 조합하면 내장 `.header` 는
   *    --sat 를 더하지 않아 상태바와 겹칠 수 있으므로, 그 경우 별도 보정이 필요하다.
   */
  fillTopSafeArea?: boolean;
  /**
   * 지정하면 패널에 `role="dialog"`·`aria-modal="true"`·`aria-label` 을 붙여 이름 있는 dialog 로 노출한다.
   * 미지정이면 역할·속성을 붙이지 않는다(기존 시트 DOM 그대로).
   */
  ariaLabel?: string;
}

// drag로 인식하기 위한 최소 이동거리(px). 이 값 미만은 click으로 간주.
const DRAG_START_THRESHOLD = 5;

const BottomSheet = ({
  isOpen,
  onClose,
  children,
  presentation = "partial",
  theme = "light",
  placementStyle = "edge",
  title,
  headerRight,
  footer,
  closeOnBackdrop = true,
  closeOnEsc = true,
  showCloseButton = true,
  closeIconColor,
  className = "",
  contentClassName = "",
  draggable = false,
  showDragHandle,
  dragCloseThreshold = 100,
  backdropOpacity,
  elevation,
  fillTopSafeArea = false,
  ariaLabel,
}: BottomSheetProps) => {
  const [shaking, setShaking] = useState(false);
  const closingRef = useRef(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const { isPressed: closePressed, ref: closeRef, pressHandlers: closeHandlers } = useTapInteraction<HTMLButtonElement>();

  // ── Drag state ──────────────────────────────────────
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // 핸들러 내부 stale closure 회피용 ref
  const startYRef = useRef(0);
  const pendingDragRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef(0);

  const shouldShowHandle = showDragHandle ?? draggable;

  // 마운트·열림 슬라이드·닫힘 대기 — 공용 useOverlayPresence(열리는 커밋에 마운트 → 강제 reflow 로 닫힌 위치
  // translateY(100%) 확정 → 다음 프레임 .open). 언마운트는 패널 전환(280ms) + 20ms 여유 = 지금 값 300ms,
  // 동작 줄이기면 즉시(보이지 않는 시트가 뒤로가기를 붙잡지 않게). 닫히는 동안 다시 열리면 타이머를 취소한다.
  const { shouldRender, isAnimating } = useOverlayPresence(isOpen, OVERLAY_PANEL_MS + 20, sheetRef);

  // 열고 닫을 때 드래그 상태 리셋. 닫혀 있는 동안은 드래그 값이 바뀌지 않으므로(아래 포인터 핸들러의 !isOpen 가드)
  // 다시 열 때 마운트가 이 리셋보다 먼저여도 시트는 닫힌 위치에서 시작한다 — 열 때의 리셋은 방어용이다.
  useEffect(() => {
    if (isOpen) closingRef.current = false;
    dragOffsetRef.current = 0;
    isDraggingRef.current = false;
    pendingDragRef.current = false;
    setDragOffset(0);
    setIsDragging(false);
  }, [isOpen]);

  // 흔들리는 중 닫힘: 닫히는 첫 커밋은 흔들림만 떼고 열린 모습(.open)을 그대로 둔 채 스타일을 확정(강제 reflow)한 뒤,
  // 다음 커밋에서 닫힌 모습으로 바꿔 닫힘 전환을 시작한다. 흔들림 해제와 닫힌 모습을 한 커밋에 바꾸면 Chromium 이
  // transform 전환을 시작하지 않아 닫힘 위치로 순간 이동하고, 흔들림을 그대로 두면(흔들림 300ms > 닫힘 280ms)
  // 제자리에서 페이드하다 끝나는 순간 사라진다. 흔들림 상태도 여기서 되돌린다 — 끝나기 전에 사라지면
  // animationend 가 오지 않아 다음에 열 때 다시 재생된다. (AlertDialog 와 같은 방식, 드래그 inline transform 과는 무관)
  const holdOpenForShake = !isOpen && shaking;
  useLayoutEffect(() => {
    if (!holdOpenForShake) return;
    void sheetRef.current?.offsetHeight;
    setShaking(false);
  }, [holdOpenForShake]);

  useBodyScrollLock(isOpen);

  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    onClose();
  }, [onClose]);

  // 배경(Dim) 탭: 닫힘 가능 sheet 는 닫고, 닫힘 불가 sheet 는 흔들림으로 강제 선택 유도(Spec 4).
  const handleBackdrop = () => {
    if (closeOnBackdrop) {
      handleClose();
    } else {
      setShaking(true);
    }
  };

  // native back(AOS 백버튼/iOS edge-swipe) = 배경 탭과 같게(B2 결정 2026-10-27).
  // 열린 순간부터 언마운트(닫힘 애니메이션 끝)까지 등록 — 닫히는 동안은 소비만 해 아래 화면으로 새지 않게 한다.
  // 배경이 보이지 않는 full 은 X 와 같다.
  const handleBack = () => {
    const action = resolveOverlayBackAction({
      isClosing: !isOpen || closingRef.current,
      isFullScreen: presentation === "full",
      backdrop: closeOnBackdrop ? "close" : "shake",
    });
    if (action === "close") handleClose();
    else if (action === "shake") setShaking(true);
  };
  useOverlayBackRegistration(isOpen, shouldRender, handleBack);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeOnEsc, handleClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    // 닫히는 동안의 배경 탭은 소비만(아래 화면으로 통과시키지 않고, 흔들림·재닫기도 없음).
    if (!isOpen) return;
    handleBackdrop();
  };

  // ── Drag handlers ───────────────────────────────────
  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggable || !isOpen || closingRef.current) return;
    // 인터랙티브 요소(버튼/링크/입력/체크박스 등)에서 시작한 포인터는 drag 대상에서 제외한다.
    // 미적용 시 버튼 press 와 sheet drag 가 같은 포인터를 두고 충돌해
    // sheet 가 pointer capture 로 press 를 가로채거나(누락) 시트가 들썩인다.
    if (
      e.target instanceof Element &&
      e.target.closest(
        'button, a, input, textarea, select, [role="checkbox"], [role="button"], [role="radio"]',
      )
    ) {
      return;
    }
    pendingDragRef.current = true;
    startYRef.current = e.clientY;
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    // 닫히는 동안은 드래그 값을 바꾸지 않는다(이미 잡은 포인터의 이동 포함) — 다시 열 때 남은 값이 없게.
    if (!draggable || !isOpen || !pendingDragRef.current) return;
    const dy = e.clientY - startYRef.current;

    // 첫 임계점 이전: drag 인식 보류
    if (!isDraggingRef.current) {
      if (Math.abs(dy) < DRAG_START_THRESHOLD) return;
      if (dy < 0) {
        // 위로 swipe는 drag close 대상 아님 — 인식 중단
        pendingDragRef.current = false;
        return;
      }
      isDraggingRef.current = true;
      setIsDragging(true);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // setPointerCapture 미지원/실패는 무시 (이벤트 캡처 없이 진행)
      }
    }

    // 위로 다시 올라간 경우 0으로 clamp
    const next = dy > 0 ? dy : 0;
    dragOffsetRef.current = next;
    setDragOffset(next);
  };

  const handlePointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pendingDragRef.current && !isDraggingRef.current) return;
    pendingDragRef.current = false;
    if (!isDraggingRef.current) return;

    const finalOffset = dragOffsetRef.current;
    isDraggingRef.current = false;
    setIsDragging(false);

    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    // 끄는 도중 밖에서 이미 닫혔으면(isOpen=false) 닫기를 다시 부르지 않는다 — 위치 정리는 닫힘 effect 가 한다.
    if (!isOpen) return;

    if (finalOffset > dragCloseThreshold) {
      // 닫기로 확정. translateY는 그대로 두면 close 애니메이션이 100%로 보낸다.
      // 단, isOpen이 false가 되기 전까지 inline transform이 우선이라 살짝 위치가 멈춰 보일 수 있어
      // 다음 프레임에 dragOffset 0으로 — 그 사이 onClose가 호출되어 자연스럽게 100%로.
      handleClose();
      requestAnimationFrame(() => {
        dragOffsetRef.current = 0;
        setDragOffset(0);
      });
    } else {
      // snap back — transition 복구된 상태에서 0으로 돌아감
      dragOffsetRef.current = 0;
      setDragOffset(0);
    }
  };

  if (!shouldRender) return null;

  const showHeader = !!(title || headerRight || showCloseButton);

  // drag 중에는 inline transform이 CSS open class의 translateY(0)을 override.
  // .dragging 클래스로 transition: none 강제.
  const sheetStyle: CSSProperties = {};
  if (elevation) {
    sheetStyle.zIndex = `calc(var(--z-modal) + 1 + ${elevation})`;
  }
  if (isDragging || dragOffset > 0) {
    sheetStyle.transform = `translateY(${dragOffset}px)`;
  }

  // document.body 로 portal — TabShell track(transform=stacking context) 밖으로 빼서
  // overlay/sheet(position:fixed)가 뷰포트 기준으로 전체 화면(하단 NavBar 포함)을 덮게 한다.
  // (z-index: --z-modal 200 > --z-navbar 50)
  return createPortal(
    <>
      <div
        className={`${styles.overlay} ${isAnimating ? styles.overlayOpen : ""} ${!isOpen ? styles.overlayClosing : ""}`}
        style={
          backdropOpacity === undefined && !elevation
            ? undefined
            : ({
                ...(backdropOpacity !== undefined
                  ? { "--bottom-sheet-backdrop-opacity": backdropOpacity }
                  : {}),
                ...(elevation
                  ? { zIndex: `calc(var(--z-modal) + ${elevation})` }
                  : {}),
              } as CSSProperties)
        }
        onClick={handleOverlayClick}
      />
      <div
        ref={sheetRef}
        className={[
          styles.bottomSheet,
          styles[presentation],
          styles[theme],
          placementStyle === "floating" ? styles.floating : styles.edge,
          fillTopSafeArea ? styles.fillTopSafeArea : "",
          isAnimating || holdOpenForShake ? styles.open : "",
          // 닫히는 동안은 시트 안 버튼·드래그 입력을 받지 않는다(재실행·재드래그 방지) — 탭은 아래 배경 막이 소비.
          !isOpen ? styles.contentClosing : "",
          isDragging ? styles.dragging : "",
          draggable ? styles.draggable : "",
          isOpen && shaking ? styles.shake : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        style={sheetStyle}
        {...(ariaLabel ? { role: "dialog", "aria-modal": true, "aria-label": ariaLabel } : {})}
        onKeyDownCapture={!isOpen ? blockActivationKeys : undefined}
        onKeyUpCapture={!isOpen ? blockActivationKeys : undefined}
        onAnimationEnd={(e) => {
          // 자식 애니메이션 버블링 무시 — sheet 자신의 shake 종료만 처리
          if (e.target === e.currentTarget) setShaking(false);
        }}
        onPointerDown={draggable ? handlePointerDown : undefined}
        onPointerMove={draggable ? handlePointerMove : undefined}
        onPointerUp={draggable ? handlePointerEnd : undefined}
        onPointerCancel={draggable ? handlePointerEnd : undefined}
      >
        {shouldShowHandle && (
          <div className={styles.dragHandle} aria-hidden="true">
            <div className={styles.dragHandleBar} />
          </div>
        )}

        {showHeader && (
          <div className={styles.header}>
            <div className={styles.headerSpacer} />
            {title && <div className={styles.headerTitle}>{title}</div>}
            <div className={styles.headerActions}>
              {headerRight}
              {showCloseButton && (
                <button
                  ref={closeRef}
                  className={`${styles.headerClose} ${closePressed ? styles.pressed : ""}`.trim()}
                  style={closeIconColor ? { color: closeIconColor } : undefined}
                  onClick={handleClose}
                  type="button"
                  aria-label="닫기"
                  {...closeHandlers}
                >
                  <CloseIcon width="24" height="24" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className={`${styles.content} ${contentClassName}`}>{children}</div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </>,
    document.body,
  );
};

export default BottomSheet;
